import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import {
  Users,
  FileText,
  CalendarCheck,
  ShieldCheck,
  ShoppingBag,
  TrendingUp,
  Percent,
  Euro,
  Target,
  Wallet,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

const STAT_TILES = [
  { label: "Clients inscrits", k: "users", icon: Users },
  { label: "Devis reçus", k: "quotes", icon: ShoppingBag },
  { label: "Contrats", k: "contrats", icon: FileText },
  { label: "Rendez-vous", k: "rdv", icon: CalendarCheck },
  { label: "Administrateurs", k: "admins", icon: ShieldCheck },
];

const GOLD = "#DCB457";
const SKY = "#7dd3fc";
const ROSE = "#fda4af";
const PIE_COLORS = ["#DCB457", "#7dd3fc", "#fda4af", "#a78bfa", "#fcd34d", "#86efac", "#fda4af", "#f0abfc"];

function StatTile({ label, value, icon: Icon, testid }) {
  return (
    <div className="card-luxe p-5" data-testid={testid}>
      <Icon size={18} strokeWidth={1.3} className="text-[#DCB457] mb-3" />
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/55">{label}</p>
      <p className="font-serif-luxe text-3xl mt-1">{value ?? "—"}</p>
    </div>
  );
}

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black/95 border border-[#DCB457]/40 px-3 py-2 text-xs" style={{ color: "#fff" }}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#DCB457] mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name} : <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
}

function RevenueTooltip({ active, payload, label, fmtEuro }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black/95 border border-[#DCB457]/40 px-3 py-2 text-xs">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#DCB457] mb-1">{label}</p>
      <p style={{ color: GOLD }}>CA : <strong>{fmtEuro(payload[0].value)}</strong></p>
    </div>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [revenue, setRevenue] = useState(null);

  useEffect(() => {
    adminApi.stats().then(setStats).catch(() => {});
    adminApi.timeline(12).then(setTimeline).catch(() => {});
    adminApi.breakdown().then(setBreakdown).catch(() => {});
    adminApi.funnel().then(setFunnel).catch(() => {});
    adminApi.revenue(12).then(setRevenue).catch(() => {});
  }, []);

  const fmtEuro = (v) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

  return (
    <div data-testid="admin-overview">
      <header className="mb-8">
        <p className="eyebrow">Administration</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
          Vue d&apos;<span className="italic gold-text">ensemble.</span>
        </h1>
        <p className="text-white/60 text-sm mt-3">
          Tableau de bord — statistiques globales, évolution mensuelle et conversion.
        </p>
      </header>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {STAT_TILES.map(({ label, k, icon: Icon }) => (
          <StatTile key={k} label={label} icon={Icon} value={stats?.[k]} testid={`stat-${k}`} />
        ))}
      </div>

      {/* Conversion metrics */}
      {timeline?.totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatTile label="Devis 12 derniers mois" value={timeline.totals.quotes} icon={TrendingUp} testid="metric-quotes-12m" />
          <StatTile label="Contrats 12 derniers mois" value={timeline.totals.contrats} icon={FileText} testid="metric-contrats-12m" />
          <StatTile label="Nouveaux clients 12 mois" value={timeline.totals.users} icon={Users} testid="metric-users-12m" />
          <StatTile label="Taux de conversion" value={`${timeline.totals.conversion_pct ?? 0}%`} icon={Percent} testid="metric-conversion" />
        </div>
      )}

      {/* Revenue KPIs */}
      {revenue?.totals && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatTile label="CA encaissé (12 mois)" value={fmtEuro(revenue.totals.revenue_ttc)} icon={Euro} testid="metric-revenue" />
          <StatTile label="Panier moyen" value={fmtEuro(revenue.totals.avg_deal_size)} icon={Wallet} testid="metric-avg-deal" />
          <StatTile label="Factures payées" value={revenue.totals.paid_count} icon={Target} testid="metric-paid-count" />
        </div>
      )}

      {/* Conversion funnel */}
      {funnel?.stages?.length ? (
        <div className="card-luxe p-6 md:p-8 mb-8" data-testid="chart-funnel">
          <div className="flex items-baseline justify-between mb-5 gap-3 flex-wrap">
            <h2 className="font-serif-luxe text-2xl">Entonnoir de conversion</h2>
            <p className="text-[11px] uppercase tracking-[0.22em] text-white/45">
              Du contact au paiement final
            </p>
          </div>
          <div className="space-y-3">
            {funnel.stages.map((st, idx) => {
              const width = Math.max(6, st.pct_of_top);
              return (
                <div key={st.key} data-testid={`funnel-stage-${st.key}`} className="group">
                  <div className="flex items-baseline justify-between text-xs mb-1.5">
                    <span className="uppercase tracking-[0.18em] text-white/70">{st.label}</span>
                    <span className="text-white/60">
                      <strong className="text-white">{st.value}</strong>
                      <span className="mx-2 text-white/30">·</span>
                      <span className="text-[#DCB457]">{st.pct_of_top}%</span>
                      {idx > 0 && st.drop_pct != null && (
                        <>
                          <span className="mx-2 text-white/30">·</span>
                          <span className="text-rose-300/80">-{st.drop_pct}% drop</span>
                        </>
                      )}
                    </span>
                  </div>
                  <div className="h-8 bg-white/[0.03] border border-white/[0.06] rounded-sm overflow-hidden">
                    <div
                      className="h-full transition-all duration-500 flex items-center px-3 text-[10px] text-black font-medium"
                      style={{
                        width: `${width}%`,
                        background: "linear-gradient(90deg, #DCB457 0%, #F4E4A1 100%)",
                      }}
                    >
                      {st.value}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Revenue over time */}
      {revenue?.timeline?.length ? (
        <div className="card-luxe p-6 md:p-8 mb-8" data-testid="chart-revenue">
          <div className="flex items-baseline justify-between mb-5 gap-3 flex-wrap">
            <h2 className="font-serif-luxe text-2xl">Chiffre d&apos;affaires mensuel</h2>
            <p className="text-[11px] uppercase tracking-[0.22em] text-white/45">
              Encaissements TTC (acomptes + soldes)
            </p>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={revenue.timeline} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(220,180,87,0.08)" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                <YAxis stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                <Tooltip content={<RevenueTooltip fmtEuro={fmtEuro} />} />
                <Bar dataKey="revenue" fill="#DCB457" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      {/* Evolution chart */}
      <div className="card-luxe p-6 md:p-8 mb-8" data-testid="chart-evolution">
        <div className="flex items-baseline justify-between mb-5 gap-3 flex-wrap">
          <h2 className="font-serif-luxe text-2xl">Évolution sur 12 mois</h2>
          <p className="text-[11px] uppercase tracking-[0.22em] text-white/45">
            Devis · Contrats · Nouveaux clients
          </p>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          {timeline?.timeline?.length ? (
            <ResponsiveContainer>
              <AreaChart data={timeline.timeline} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={GOLD} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradSky" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SKY} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={SKY} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradRose" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={ROSE} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={ROSE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(220,180,87,0.08)" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                <YAxis stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<TooltipBox />} />
                <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.65)" }} />
                <Area name="Devis" type="monotone" dataKey="quotes" stroke={GOLD} strokeWidth={2} fill="url(#gradGold)" />
                <Area name="Contrats" type="monotone" dataKey="contrats" stroke={SKY} strokeWidth={2} fill="url(#gradSky)" />
                <Area name="Nouveaux clients" type="monotone" dataKey="users" stroke={ROSE} strokeWidth={2} fill="url(#gradRose)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-white/45 text-sm italic">Chargement du graphique…</p>
          )}
        </div>
      </div>

      {/* Breakdown pies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-luxe p-6 md:p-8" data-testid="chart-types">
          <h2 className="font-serif-luxe text-2xl mb-1">Devis par type d&apos;événement</h2>
          <p className="text-[11px] uppercase tracking-[0.22em] text-white/45 mb-5">
            Répartition de votre activité
          </p>
          <div style={{ width: "100%", height: 280 }}>
            {breakdown?.by_type?.length ? (
              <ResponsiveContainer>
                <PieChart>
                  <Tooltip content={<TooltipBox />} />
                  <Pie data={breakdown.by_type} dataKey="value" nameKey="name" outerRadius={95} innerRadius={50} stroke="#0a0a0a" strokeWidth={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {breakdown.by_type.map((_, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-white/45 text-sm italic">Aucun devis pour l&apos;instant.</p>
            )}
          </div>
        </div>

        <div className="card-luxe p-6 md:p-8" data-testid="chart-status">
          <h2 className="font-serif-luxe text-2xl mb-1">Devis par statut</h2>
          <p className="text-[11px] uppercase tracking-[0.22em] text-white/45 mb-5">
            Avancement de votre pipeline
          </p>
          <div style={{ width: "100%", height: 280 }}>
            {breakdown?.by_status?.length ? (
              <ResponsiveContainer>
                <PieChart>
                  <Tooltip content={<TooltipBox />} />
                  <Pie data={breakdown.by_status} dataKey="value" nameKey="name" outerRadius={95} innerRadius={50} stroke="#0a0a0a" strokeWidth={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {breakdown.by_status.map((_, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-white/45 text-sm italic">Aucun devis pour l&apos;instant.</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <Link to="/admin/clients" data-testid="admin-go-clients" className="btn-gold inline-flex items-center gap-2">
          Gérer les clients →
        </Link>
      </div>
    </div>
  );
}
